import { PhoneNumberUtil } from "google-libphonenumber";

import { STRING_BOOLEAN, StringBoolean } from "constants/primitive";
import { PROPERTY_VISIBILITY_OPTIONS } from "constants/property";
import { PublicStatus, PUBLIC_STATUS } from "constants/status";
import { Maybe } from "graphql/type";

const { TRUE, FALSE } = STRING_BOOLEAN;

/**
 * 数値をカンマ区切りする
 * @param value
 * @param suffix
 * @returns
 */
export const convertNumberToLocaleString = (value: number, suffix = "円") => {
  return `${value?.toLocaleString()}${suffix}`;
};

/**
 * 文字数字から公開・非公開を返す
 * @param input string
 * @returns 公開 or 非公開
 */
export const convertPublicStatus = (input: string): PublicStatus => {
  const findStatus = PROPERTY_VISIBILITY_OPTIONS.find((option) => option.value === input);

  if (!findStatus) return PROPERTY_VISIBILITY_OPTIONS[0].label as PublicStatus;

  return findStatus.label as PublicStatus;
};

/**
 * 公開・非公開から文字数字を返す
 * @param value boolean type
 * @returns StringBoolean type
 */
export const booleanToString = (value: boolean): StringBoolean => (value ? TRUE : FALSE);
/**
 * undefined or null or booleanをStringBooleanに変換する
 * @param field
 * @param defaultValue
 * @returns StringBoolean
 */
export const booleanToStringBoolean = (
  field: undefined | null | boolean,
  defaultValue?: StringBoolean
): StringBoolean => {
  return field == null ? defaultValue ?? FALSE : field ? TRUE : FALSE;
};

// StringBooleanからbooleanに変換する
/**
 * StringBooleanをbooleanに変換する
 * @param value StringBoolean type
 * @returns boolean type
 */
export const stringBooleanToBoolean = (value: StringBoolean): boolean => value === TRUE;

/**
 * 文字列をスネークケースに変換する
 * @param value ex) helloWorld
 * @returns ex) HELLO_WORLD
 */
export const toUpperSnakeCase = (input: string | undefined | null): string => {
  if (!input) return "";
  const value = input.replace(/([A-Z])/g, (match) => `_${match}`).toUpperCase();
  return value;
};

/**
 * "0" | "1" をboolean型に変換 0:false 1:true
 * @param number "0" | "1"
 * @returns boolean
 */
export const stringNumberToBoolean = (num: "0" | "1"): boolean => num === "1";

/**
 * false | true を"0" | "1"に変換 false:0 true:1
 * @param boolean
 * @returns "0" | "1"
 */
export const booleanToStringNumber = (bool: boolean): "0" | "1" => (bool ? "1" : "0");

/**
 * ナンバープレート情報の含まれたjsonからナンバープレート文字列を作成する
 * @param carNumberJson { furigana, number, region, regionCord }
 * @returns ナンバープレート文字列
 */
export const createNumberPlateString = ({
  carNumberJson: { furigana, number, region, regionCord },
}: {
  carNumberJson: { number: string; region: string; furigana: string; regionCord: string };
}): string => {
  return `${region} ${regionCord} ${furigana} ${number}`;
};

/**
 * ファイル名をランダムな文字列で生成
 * @example
 * generateRandomFileName("sample.pdf")
 * => "b3f0c9e0-5b7e-4e2e-9e1a-5e1e1e1e1e1e.pdf"
 */
export const generateRandomFileName = (fileName: string): string => {
  const extension = fileName.split(".").pop() ?? "";
  return `${crypto.randomUUID()}.${extension}`;
};

/*
 * 文字列を任意のJavaScriptのオブジェクトリテラルに変換
 * @param str
 * @returns
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const parseStringToObject = <T extends Record<string, any>>(str: string): T => {
  let parsedObj: T;

  // 文字列をオブジェクトにパース
  try {
    parsedObj = JSON.parse(str) as T;
  } catch (error) {
    console.error("Invalid JSON format:", error);
    return {} as T;
  }

  return parsedObj;
};

/**
 * 文字列がnull or undefined or 空文字かどうかを判定
 * @example
 * isEmptyString("") // true
 * isEmptyString(null) // true
 * isEmptyString(undefined) // true
 * isEmptyString(" ") // true
 * isEmptyString("a") // false
 */
export const isEmptyString = (value?: string | Maybe<string>): boolean => {
  return value == null || value.trim().length === 0;
};

/**
 * 日本の電話番号を3-4-4形式にフォーマットします。
 * @example
 * formatPhoneNumber("09012345678") // "090-1234-5678"
 * formatPhoneNumber("0296438612") // "0296-43-8612"
 */
export const formatPhoneNumber = (phoneNumber?: string | null): string => {
  if (!phoneNumber) return "";
  const expectPureNumber = Number(phoneNumber.trim().replaceAll("-", ""));
  if (isNaN(expectPureNumber) || 9 > `${expectPureNumber}`.length) return "";
  const phoneUtil = PhoneNumberUtil.getInstance();
  const number = phoneUtil.parseAndKeepRawInput(phoneNumber, "JP");
  return phoneUtil.formatInOriginalFormat(number);
};

/**
 * Converts full-width characters (全角) to half-width characters (半角)
 * @param str The input string containing full-width characters
 * @returns The converted string with half-width characters
 * @example
 * "トキョ１－２－３" => "トキョ1-2-3"
 */
export const convertFullToHalf = (str: string): string => {
  return str.replace(/[Ａ-Ｚａ-ｚ０-９－]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0)).replace(/－/g, "-");
};

export const convertPublicSpaceGroupStatus = (input: string): PublicStatus => {
  return input === "1" ? PUBLIC_STATUS.PUBLISH : PUBLIC_STATUS.PRIVATE;
};
